import { useState } from 'react';
import WarnExclamation from 'svg/WarnExclamation';
import { Button } from 'components/Button';
import classNames from 'styles/utils/classNames';
import RequesterInfo from './RequesterInfo';
import { type HostPlayRequest, formatAvailableOn } from './types';

interface Props {
  requests: HostPlayRequest[];
  /** Opens the request review modal for the tapped requester (wired by the parent). */
  onSeeRequest: (request: HostPlayRequest) => void;
  /** Notice above the rows — e.g. the "request limit reached" copy on the activity card. */
  notice?: string;
  /** Show only the first N rows and a button for the rest. Pair with `onViewAll`
   *  to send the viewer elsewhere, or leave it off to expand the card in place. */
  visibleLimit?: number;
  onViewAll?: () => void;
  /** From lg up, Figma's stacked desktop card however wide it gets (activity stage). */
  isStackedAtDesktop?: boolean;
  className?: string;
}

/**
 * Shared orange "Requests" card chrome (fire base + the top-right radial highlight
 * and glow) — mirrors `CARD_CLASSES` in the home screen's FindPlayersSection.
 */
const REQUESTS_CARD_CLASSES =
  'rounded-3xl bg-brand-fire-600 bg-[radial-gradient(140%_190%_at_80%_18%,#FF6610_0%,#F54519_40%,rgba(223,67,32,0)_88%)] shadow-[0px_4px_20px_0px_rgba(255,92,51,0.5)]';

/**
 * The embedded orange card of pending requests shown inside a host's game /
 * availability detail modal and activity cards. Game requests show the requester's
 * skill pills; availability requests show their chosen slot as a sub-label instead.
 * Each row's "See request" button opens the review modal via `onSeeRequest`. Layout
 * switches on the card's own width (container query), since it's embedded in modals
 * and cards of very different widths.
 */
export default function HostRequestsCard({
  requests,
  onSeeRequest,
  notice,
  visibleLimit,
  onViewAll,
  isStackedAtDesktop = false,
  className,
}: Props) {
  // Without `onViewAll` the extra rows open here, so the card owns that state.
  const [isExpanded, setIsExpanded] = useState(false);
  const isCollapsed = Boolean(visibleLimit) && !(isExpanded && !onViewAll);
  const visible = isCollapsed ? requests.slice(0, visibleLimit) : requests;
  const hasMore = visible.length < requests.length;
  const canToggle = hasMore || (isExpanded && !onViewAll);

  return (
    // With no query container the `@xl:` side-by-side variants never match.
    <div className={isStackedAtDesktop ? 'max-lg:@container' : '@container'}>
      <div
        className={classNames(
          REQUESTS_CARD_CLASSES,
          'flex flex-col gap-ds-sm p-ds-lg @xl:flex-row @xl:items-start @xl:gap-ds-xl @xl:p-ds-xl',
          isStackedAtDesktop && 'lg:gap-ds-md lg:rounded-[1.25rem]',
          className,
        )}
      >
        <h3
          className={classNames(
            'typography-product-card-title-mobile @xl:typography-product-heading-compact-desktop text-color-text-lightmode-invert @xl:shrink-0 @xl:self-center',
            isStackedAtDesktop && 'lg:typography-product-card-title-desktop',
          )}
        >
          Requests: {requests.length}
        </h3>
        <div
          className={classNames(
            'h-px w-full bg-color-border-input-lightmode @xl:h-auto @xl:w-px @xl:self-stretch @xl:bg-color-border-input-lightmode/40',
            isStackedAtDesktop && 'lg:hidden',
          )}
        />

        <div
          className={classNames(
            'flex min-w-0 flex-col gap-ds-sm @xl:flex-1 @xl:gap-ds-md',
            isStackedAtDesktop && 'lg:gap-ds-md',
          )}
        >
          {notice && (
            <div className="flex items-center gap-ds-sm rounded-lg bg-color-text-lightmode-invert/10 p-ds-sm">
              <WarnExclamation className="h-5 w-5 shrink-0 text-color-text-lightmode-invert" />
              <p className="typography-product-button-label-xs text-color-text-lightmode-invert">
                {notice}
              </p>
            </div>
          )}

          <div
            className={classNames(
              'flex flex-col gap-ds-sm divide-y divide-color-text-lightmode-invert/20 @xl:gap-ds-md',
              isStackedAtDesktop && 'lg:gap-ds-md',
            )}
          >
            {visible.map((request) => (
              <div
                key={request.id}
                className={classNames(
                  'flex items-center gap-ds-md pt-ds-sm first:pt-0 @xl:pt-ds-md @xl:first:pt-0',
                  isStackedAtDesktop && 'lg:pt-ds-md lg:first:pt-0',
                )}
              >
                <div className="min-w-0 flex-1">
                  <RequesterInfo
                    player={request.player}
                    avatarClassName="size-10 @xl:size-12"
                    invertText
                    subLabel={request.availableOn && formatAvailableOn(request.availableOn)}
                  />
                </div>
                <Button
                  variant="primary-light"
                  size="sm"
                  isInline
                  onClick={() => onSeeRequest(request)}
                  className="shrink-0 whitespace-nowrap"
                >
                  See request
                </Button>
              </div>
            ))}
          </div>

          {canToggle &&
            (onViewAll ? (
              <Button variant="secondary-invert" size="sm" onClick={onViewAll}>
                View all
              </Button>
            ) : (
              <Button
                variant="secondary-invert"
                size="sm"
                isInline
                onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
                className="min-w-[6.25rem] self-end whitespace-nowrap"
              >
                {isExpanded ? 'View less' : 'View all'}
              </Button>
            ))}
        </div>
      </div>
    </div>
  );
}
